import React, { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { usePageContent } from '../hooks/usePageContent';

const DEFAULT_SITE = {
    name: 'Chaw Thinn',
    title: 'Product Manager',
};

const NAV = [
    { to: '/', label: 'Home' },
    { to: '/work', label: 'Work' },
    { to: '/about', label: 'About' },
    { to: '/writing', label: 'Writing' },
    { to: '/contact', label: 'Contact' },
];

export default function Header() {
    const { theme, toggleTheme } = useTheme();
    const { content: siteContent } = usePageContent('site');
    const site = siteContent || DEFAULT_SITE;
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const location = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 4);
        onScroll();
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    useEffect(() => {
        setOpen(false);
    }, [location.pathname]);

    return (
        <header
            className={
                'sticky top-0 z-50 w-full transition-all duration-300 ' +
                (scrolled
                    ? 'bg-bg/90 backdrop-blur-md border-b border-line shadow-[0_1px_0_rgba(0,0,0,0.03)]'
                    : 'bg-transparent border-b border-transparent')
            }
        >
            <div className="container-page flex items-center justify-between gap-4 h-20">
                <Link to="/" className="flex items-center gap-3 group shrink-0" aria-label="Home">
                    <img
                        src="/favicon.svg"
                        alt=""
                        aria-hidden="true"
                        className="w-11 h-11 shrink-0"
                    />
                    <span className="flex flex-col leading-tight min-w-0">
                        <span className="font-display font-semibold tracking-tight text-[15px] text-fg group-hover:text-accent transition-colors truncate">
                            {site.name || DEFAULT_SITE.name}
                        </span>
                        <span className="text-[12px] text-fg-muted truncate">
                            {site.title || DEFAULT_SITE.title}
                        </span>
                    </span>
                </Link>

                <nav className="hidden md:flex items-center gap-8" aria-label="Primary">
                    {NAV.map((item) => (
                        <NavLink
                            key={item.to}
                            to={item.to}
                            end={item.to === '/'}
                            className={({ isActive }) =>
                                'text-[14px] font-medium transition-colors duration-200 ' +
                                (isActive ? 'text-fg' : 'text-fg-muted hover:text-fg')
                            }
                        >
                            {item.label}
                        </NavLink>
                    ))}
                </nav>

                <div className="flex items-center gap-2">
                    <button
                        type="button"
                        onClick={toggleTheme}
                        aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
                        className="inline-flex items-center justify-center w-10 h-10 rounded-full border border-line text-fg-muted hover:text-fg hover:border-fg transition-colors"
                    >
                        {theme === 'dark' ? <SunIcon /> : <MoonIcon />}
                    </button>
                    <button
                        type="button"
                        onClick={() => setOpen((o) => !o)}
                        aria-label="Toggle menu"
                        aria-expanded={open}
                        className="md:hidden inline-flex items-center justify-center w-10 h-10 rounded-full border border-line text-fg-muted hover:text-fg hover:border-fg transition-colors"
                    >
                        {open ? <CloseIcon /> : <MenuIcon />}
                    </button>
                </div>
            </div>

            {/* Mobile nav */}
            {open && (
                <div className="md:hidden border-t border-line bg-bg/95 backdrop-blur-md">
                    <nav className="container-page py-4 flex flex-col gap-1" aria-label="Mobile">
                        {NAV.map((item) => (
                            <NavLink
                                key={item.to}
                                to={item.to}
                                end={item.to === '/'}
                                className={({ isActive }) =>
                                    'py-3 min-h-[44px] flex items-center text-[15px] font-medium ' +
                                    (isActive ? 'text-fg' : 'text-fg-muted')
                                }
                            >
                                {item.label}
                            </NavLink>
                        ))}
                    </nav>
                </div>
            )}
        </header>
    );
}

function SunIcon() {
    return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <circle cx="12" cy="12" r="4" />
            <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
        </svg>
    );
}

function MoonIcon() {
    return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
        </svg>
    );
}

function MenuIcon() {
    return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" aria-hidden="true">
            <path d="M4 7h16M4 12h16M4 17h16" />
        </svg>
    );
}

function CloseIcon() {
    return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" aria-hidden="true">
            <path d="M6 6l12 12M18 6L6 18" />
        </svg>
    );
}
